import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocketStore } from '../stores/websocketStore';
import { createWebSocketHandler } from '../services/websocketHandler';

// ==========================================
// Types
// ==========================================

interface QueueContextValue {
  shopId: string;
}

// ==========================================
// Context
// ==========================================

const QueueContext = createContext<QueueContextValue | null>(null);

// ==========================================
// Provider
// ==========================================

interface QueueProviderProps {
  shopId: string;
  children: ReactNode;
}

/**
 * QueueProvider now only provides shopId context and manages WebSocket connection.
 * All queue state is managed by TanStack Query (useQueueQuery hook).
 * WebSocket messages update the Query cache directly via websocketHandler.
 */
export function QueueProvider({ shopId, children }: QueueProviderProps) {
  const queryClient = useQueryClient();
  const { connect, disconnect } = useWebSocketStore();

  // Connect WebSocket on mount, disconnect on unmount
  useEffect(() => {
    // Create message handler that updates Query cache
    const messageHandler = createWebSocketHandler(queryClient, shopId);

    // Connect to WebSocket
    connect(shopId, messageHandler);

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [shopId, queryClient, connect, disconnect]);

  return (
    <QueueContext.Provider value={{ shopId }}>
      {children}
    </QueueContext.Provider>
  );
}

// ==========================================
// Hook
// ==========================================

/**
 * Returns the current shop ID from context.
 * Use useQueueQuery(shopId) to get queue data.
 */
export function useQueueContext(): QueueContextValue {
  const context = useContext(QueueContext);
  if (!context) {
    throw new Error('useQueueContext must be used within QueueProvider');
  }
  return context;
}

/**
 * @deprecated Use useQueueQuery() from hooks/useQueueQuery.ts instead.
 * This hook is kept for backwards compatibility during migration.
 */
export function useQueue() {
  throw new Error(
    'useQueue is deprecated. Use useQueueQuery() from hooks/useQueueQuery.ts instead.'
  );
}
